import '../../i18n/autoTranslate';
import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ConfigurationSummary = ({
  embedded = false,
  configuration,
  saveConfig,
  onConfigurationChange,
  onStartInterview,
  onEditSection,
  className = '',
}) => {
  const getSummaryData = () => {
    const {
      selectedType = 'behavioral',
      selectedIndustry = 'technology',
      selectedRole = 'software_engineer',
      selectedCompanyType = 'startup',
      personality = {},
      selectedCategories = [],
      selectedFormat = 'video',
      duration = 30,
    } = configuration;

    return {
      interviewType: {
        icon: 'MessageSquare',
        label: 'Interview Type',
        value: selectedType?.charAt(0)?.toUpperCase() + selectedType?.slice(1)?.replace('_', ' '),
        section: 'type',
      },
      targeting: {
        icon: 'Target',
        label: 'Role Targeting',
        value: `${selectedRole?.replace('_', ' ')} in ${selectedIndustry}`,
        section: 'targeting',
      },
      interviewer: {
        icon: 'Bot',
        label: 'AI Interviewer',
        value: personality?.selectedPersona
          ? personality?.selectedPersona?.id?.charAt(0)?.toUpperCase() +
            personality?.selectedPersona?.id?.slice(1) +
            ' Persona'
          : 'Professional Persona',
        section: 'personality',
      },
      questions: {
        icon: 'HelpCircle',
        label: 'Question Sources',
        value:
          selectedCategories?.length > 0
            ? `${selectedCategories?.length} categories selected`
            : 'Default question set',
        section: 'questions',
      },
      format: {
        icon: 'Monitor',
        label: 'Session Format',
        value: `${selectedFormat?.charAt(0)?.toUpperCase() + selectedFormat?.slice(1)} - ${duration} minutes`,
        section: 'format',
      },
    };
  };

  const summaryData = getSummaryData();

  const getEstimatedQuestions = () => {
    const baseQuestions = Math.max(5, Math.floor(configuration.duration / 3));
    const multiplier = configuration.enableFollowUps ? 1.5 : 1;
    return Math.floor(baseQuestions * multiplier);
  };

  React.useEffect(() => {
    const estimated = getEstimatedQuestions();
    if (configuration.estimatedQuestions !== estimated) {
      onConfigurationChange({
        ...configuration,
        estimatedQuestions: estimated,
      });
    }
  }, [configuration.duration, configuration.enableFollowUps]);

  const getSessionFeatures = () => {
    const features = [];

    if (configuration?.enableFollowUps) features?.push('Dynamic follow-up questions');
    if (configuration?.surpriseQuestions) features?.push('Surprise questions included');
    if (configuration?.starMethodEnforcement) features?.push('STAR method guidance');
    if (configuration?.selectedFormat === 'video') features?.push('Video analysis & feedback');
    if (configuration?.adaptiveDifficulty) features?.push('Adaptive difficulty adjustment');

    return features?.length > 0 ? features : ['Standard interview simulation'];
  };

  const panel =
    'relative overflow-hidden rounded-3xl border border-white/10 bg-[#0b0f16]/60 backdrop-blur-xl shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_18px_60px_-40px_rgba(56,189,248,0.18)]';

  const headIcon =
    'flex items-center justify-center w-10 h-10 rounded-2xl border border-white/10 bg-gradient-to-br from-sky-500/15 via-violet-500/10 to-emerald-500/10 shadow-[0_0_22px_rgba(56,189,248,0.20)]';

  return (
    <div className={`transition-all duration-200 ease-smooth space-y-8 ${className}`}>
      {!embedded && (
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="flex items-center justify-center w-10 h-10 rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl">
              <img src="/check_hdjdze.png" alt="check" className="w-10 h-10" />
            </div>
            <h2 className="text-2xl font-semibold tracking-tight text-white">Review Your Setup</h2>
          </div>
          <p className="text-white/60">Confirm your interview configuration before starting.</p>
        </div>
      )}

      {/* Configuration Summary */}
      <div className="space-y-3">
        {Object.entries(summaryData)?.map(([key, item], idx) => (
          <motion.div
            key={key}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, delay: idx * 0.03 }}
            className={`${panel} p-4 hover:border-white/16 hover:bg-white/[0.04] transition-all duration-300`}
          >
            <div className="pointer-events-none absolute inset-0 opacity-60 [background:radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.08),transparent_60%)]" />
            <div className="relative flex items-center justify-between gap-4">
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-12 h-12 rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl flex items-center justify-center shadow-[0_0_18px_rgba(56,189,248,0.18)] flex-shrink-0">
                  <Icon name={item?.icon} size={20} className="text-white" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-medium text-white">{item?.label}</h3>
                  <p className="text-sm text-white/60 truncate">{item?.value}</p>
                </div>
              </div>

              <Button
                variant="ghost"
                size="sm"
                iconName="Edit3"
                onClick={() => onEditSection(item?.section)}
                className="text-white/60 hover:text-white hover:bg-white/[0.06]"
              >
                Edit
              </Button>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Session Overview */}
      <div className={`${panel} p-6`}>
        <div className="pointer-events-none absolute inset-0 opacity-60 [background:radial-gradient(circle_at_25%_25%,rgba(56,189,248,0.16),transparent_60%)]" />
        <h3 className="relative text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <span className={headIcon}>
            <Icon name="Info" size={18} className="text-white" />
          </span>
          <span>Session Overview</span>
        </h3>

        <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-white mb-1">
              {getEstimatedQuestions()}
            </div>
            <div className="text-sm text-white/60">Estimated Questions</div>
          </div>

          <div className="text-center">
            <div className="text-3xl font-bold text-white mb-1">
              {configuration?.duration || 30}
            </div>
            <div className="text-sm text-white/60">Minutes Duration</div>
          </div>

          <div className="text-center">
            <div className="text-3xl font-bold text-white mb-1">
              {configuration?.selectedCategories?.length || 1}
            </div>
            <div className="text-sm text-white/60">Question Categories</div>
          </div>
        </div>
      </div>

      {/* Session Features */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className={headIcon}>
            <Icon name="Star" size={16} className="text-white" />
          </div>
          <h3 className="text-lg font-semibold text-white">Session Features</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {getSessionFeatures()?.map((feature, index) => (
            <div
              key={index}
              className={`${panel} p-3 hover:border-white/16 hover:bg-white/[0.04] transition-all duration-300`}
            >
              <div className="pointer-events-none absolute inset-0 opacity-60 [background:radial-gradient(circle_at_20%_20%,rgba(16,185,129,0.14),transparent_60%)]" />
              <div className="relative flex items-center gap-3">
                <Icon name="CheckCircle" size={16} className="text-emerald-300" />
                <span className="text-sm text-white/85">{feature}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tips */}
      <div className={`${panel} p-6`}>
        <div className="pointer-events-none absolute inset-0 opacity-60 [background:radial-gradient(circle_at_25%_25%,rgba(167,139,250,0.14),transparent_60%)]" />
        <h3 className="relative text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <span className={headIcon}>
            <Icon name="Lightbulb" size={18} className="text-white" />
          </span>
          <span>Pre-Interview Tips</span>
        </h3>

        <div className="relative space-y-3 text-sm text-white/65">
          <div className="flex items-start gap-3">
            <Icon name="Mic" size={16} className="text-sky-200 mt-0.5" />
            <span>Test your microphone and camera before starting the session.</span>
          </div>
          <div className="flex items-start gap-3">
            <Icon name="Wifi" size={16} className="text-sky-200 mt-0.5" />
            <span>Ensure stable internet connection for uninterrupted practice.</span>
          </div>
          <div className="flex items-start gap-3">
            <Icon name="Coffee" size={16} className="text-sky-200 mt-0.5" />
            <span>Find a quiet, well-lit space for optimal interview simulation.</span>
          </div>
          <div className="flex items-start gap-3">
            <Icon name="Clock" size={16} className="text-sky-200 mt-0.5" />
            <span>Allow extra time after the session to review your feedback.</span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col md:flex-row gap-4 pt-2">
        <Button
          variant="outline"
          size="lg"
          iconName="ArrowLeft"
          iconPosition="left"
          className="flex-1 border-white/10 hover:border-white/20 text-white"
          onClick={() => onEditSection('format')}
        >
          Back to Settings
        </Button>

        <Button
          variant="default"
          size="lg"
          iconName="Play"
          iconPosition="right"
          className="flex-1 bg-gradient-to-r from-emerald-500 via-sky-500 to-violet-500 hover:from-emerald-400 hover:via-sky-400 hover:to-violet-400 text-white shadow-[0_18px_55px_-35px_rgba(56,189,248,0.75)]"
          onClick={onStartInterview}
        >
          Start Interview Session
        </Button>
      </div>

      {/* Save */}
      <div className="text-center pt-4 border-t border-white/10">
        <Button
          onClick={saveConfig}
          variant="ghost"
          size="sm"
          iconName="Save"
          className="text-white/60 hover:text-white hover:bg-white/[0.06]"
        >
          Save Configuration for Later
        </Button>
      </div>
    </div>
  );
};

export default ConfigurationSummary;
